/**
 * Уведомления о результате действий (приёмка, замечание 36): после каждой команды и
 * скачивания файла внизу справа появляется строка «что произошло». Без библиотек:
 * модульный список подписчиков + компонент, смонтированный в оболочке.
 */
import { useEffect, useState } from 'react';
import { PREFERRED_CHANNEL } from './labels';
import type { PreferredChannel } from './types';

export interface Toast { id: number; text: string; tone: 'ok' | 'warn' | 'error' }
type Listener = (t: Toast) => void;
const listeners = new Set<Listener>();
let seq = 0;

export function toast(text: string, tone: Toast['tone'] = 'ok') {
  const t = { id: ++seq, text, tone };
  for (const l of listeners) l(t);
}

/** Текст уведомления по операции и её результату. Неизвестная операция → «Сохранено». */
export function messageFor(operation: string, data: unknown, payload: Record<string, unknown>): string | null {
  const d = (data ?? {}) as Record<string, unknown>;
  const pid = typeof d['public_id'] === 'string' ? d['public_id'] : '';
  const st = d['status'];
  switch (operation) {
    case 'auth.login': case 'auth.logout': case 'auth.refresh': return null;
    case 'orders.create': case 'orders.create_full': return `Заказ ${pid} создан${typeof payload['comment'] === 'string' && /^Повтор заказа ORD/.test(payload['comment']) ? ` (${payload['comment']})` : ''}`;
    case 'orders.update': return 'Заказ сохранён';
    case 'inquiries.to_order': return `Расчёт открыт: черновик заказа ${typeof d['order_public_id'] === 'string' ? d['order_public_id'] : ''} — проверьте цены и отправьте КП`.trim();
    case 'inquiries.send_quote': return 'КП отправлено: клиент увидит его в кабинете, файл КП — в документах заказа';
    case 'inquiries.request_sizes': return 'Просьба указать размеры отправлена клиенту в кабинет';
    case 'inquiries.reject': return 'Заявка отклонена; клиент увидит причину в кабинете';
    // Этап 22: сервис отвечает, куда ушло сообщение — в Telegram (очередь бота), письмом на почту или в кабинет на сайте.
    case 'orders.message_add': case 'dialogs.send': return d['channel'] === 'telegram' ? 'Отправлено в Telegram' : d['channel'] === 'email' ? 'Отправлено письмом' : 'Отправлено в кабинет: клиент увидит сообщение на сайте';
    case 'messages.set_order': return payload['clear'] ? 'Метка заказа снята' : 'Сообщение привязано к заказу';
    case 'contacts.link': return 'Контакт привязан к компании';
    case 'contacts.set_preferred_channel': { const ch = d['preferred_channel'] as PreferredChannel | null | undefined; return `Канал связи: ${ch && PREFERRED_CHANNEL[ch] ? PREFERRED_CHANNEL[ch].label : 'не выбран'}`; }
    case 'messaging.set_settings': return 'Настройки переписки сохранены';
    case 'messaging.set_template': return 'Шаблон сохранён';
    case 'messaging.pin_cabinet_all': return `Карточка «Ваш кабинет»: поставлено в очередь ${typeof d['sent'] === 'number' ? d['sent'] : 0}, уйдёт в рабочее время`;
    case 'messaging.pin_cabinet': return d['sent'] ? 'Карточка «Ваш кабинет» отправлена и будет закреплена' : 'Карточка уже была отправлена этому клиенту';
    case 'telegram.set_webhook': return `События бота направлены на ${typeof d['url'] === 'string' ? d['url'] : 'этот сервер'}`;
    case 'orders.cancel': return 'Заказ отменён';
    case 'orders.send_quote': return 'КП отправлено; файл КП добавлен в документы заказа';
    case 'orders.confirm': return `Заказ подтверждён: план материала посчитан${Array.isArray(d['purchase_tasks']) && (d['purchase_tasks'] as unknown[]).length ? `, заданий на закупку: ${(d['purchase_tasks'] as unknown[]).length}` : ''}${Array.isArray(d['jobs']) && (d['jobs'] as unknown[]).length ? `, заданий цехам: ${(d['jobs'] as unknown[]).length}` : ''}`;
    case 'orders.recompute_plan': return 'План материала пересчитан';
    case 'orders.mark_paid': return 'Оплата отмечена';
    case 'orders.launch': return 'Заказ запущен в производство: ткань остаётся в брони, спишется при взвешивании рулонов';
    case 'orders.ship': return `Отгружено клиенту${(d['shipment'] as { public_id?: string } | undefined)?.public_id ? ` · ${(d['shipment'] as { public_id: string }).public_id}` : ''}`;
    case 'orders.close': return 'Заказ закрыт';
    case 'positions.add': return 'Позиция добавлена в заказ';
    case 'positions.update': return 'Позиция сохранена';
    case 'positions.remove': return 'Позиция убрана';
    case 'positions.set_sizes': return 'Размеры сохранены';
    case 'decorations.add': return 'Нанесение добавлено';
    case 'decorations.update': return 'Нанесение сохранено';
    case 'decorations.remove': return 'Нанесение убрано';
    case 'services.set': return 'Доп. услуга сохранена';
    case 'services.remove': return 'Доп. услуга убрана';
    case 'jobs.create': return `Задание ${pid} создано`;
    case 'jobs.update': return 'Задание сохранено';
    case 'jobs.mark_sent': return st === 'rework_in_progress' ? 'Переделка отдана цеху: задание в процессе переделки' : d['kind'] === 'decoration' ? 'Задание передано подрядчику по нанесению; файл «Задание на нанесение» добавлен в документы заказа' : 'Задание передано цеху; файл задания добавлен в документы заказа';
    case 'jobs.mark_returned': return d['kind'] === 'decoration' ? 'Изделия забраны у подрядчика по нанесению: задание принято (ОТК общий — по партии заказа)' : 'Партия забрана у цеха и ждёт ОТК';
    case 'orders.issue_invoice': return `Счёт № ${d['number'] ?? ''} выставлен${d['document'] ? ' и добавлен в документы заказа' : ''}`;
    case 'invoices.cancel': return 'Счёт аннулирован';
    case 'settings.set': return 'Реквизиты сохранены';
    case 'stock.stocktake': return `Пересчёт записан: скорректировано рулонов — ${Array.isArray(d['changed']) ? (d['changed'] as unknown[]).length : 0}, без изменений — ${d['unchanged'] ?? 0}`;
    case 'jobs.inspect': return st === 'accepted' ? `ОТК пройден: принято ${d['accepted'] ?? ''} шт` : `ОТК: принято ${d['accepted'] ?? ''} шт, на переделку ${d['rejected'] ?? ''} шт; задание на переделку добавлено в документы`;
    case 'jobs.rework_accept': return `Переделка принята${d['rework_qty'] ? `: ${d['rework_qty']} шт` : ''}; задание принято целиком`;
    case 'jobs.accept': return 'Задание принято';
    case 'jobs.cancel': return 'Задание отменено';
    case 'purchases.create': return `Задание на закупку ${pid} создано`;
    case 'purchases.update': return 'Задание на закупку сохранено';
    case 'purchases.mark_ordered': return d['amount'] ? `Заказано у поставщика: ${d['amount']} ₽ записано в экономику заказа` : 'Отмечено: материал заказан у поставщика';
    case 'fx.set': return 'Курс сохранён';
    case 'checklist.add': return 'Пункт добавлен в чек-лист';
    case 'checklist.toggle': return payload['done'] ? 'Пункт чек-листа отмечен' : 'Отметка снята';
    case 'checklist.remove': return 'Пункт убран из чек-листа';
    case 'purchases.cancel': return 'Задание на закупку отменено';
    case 'lots.receive': return `Принято на склад: партия ${pid}${d['total_kg'] !== undefined ? ` · ${d['total_kg']} ${d['unit'] === 'pcs' ? 'шт' : d['unit'] === 'm' ? 'м' : 'кг'}` : ''}${payload['purchase_task_id'] ? '; задание на закупку закрыто' : ''}`;
    case 'stock.adjust': return 'Корректировка остатка записана';
    case 'rolls.take_to_cut': return `${d['public_id'] ?? 'Рулон'} на раскрое — после раскроя взвесьте и напечатайте этикетку`;
    case 'rolls.weigh': return d['state'] === 'used' ? `${d['public_id']} списан целиком${d['order_public_id'] ? ` на ${d['order_public_id']}` : ''}` : `Списано ${d['used_kg']} кг${d['order_public_id'] ? ` на ${d['order_public_id']}` : ''} · ${d['public_id']} теперь ${d['state'] === 'rest' ? 'остаток' : 'вскрыт'} ${d['current_kg']} кг`;
    case 'rolls.to_workshop': return `${d['public_id']} отдан цеху «${d['workshop']}»`;
    case 'rolls.mark_printed': return `Этикетки отправлены на печать: ${d['printed']}`;
    case 'stock.fitting_move': return `${Number(d['delta']) > 0 ? 'Приход' : 'Расход'} фурнитуры записан: на складе ${d['stock']} ${d['unit'] === 'pcs' ? 'шт' : d['unit'] === 'm' ? 'м' : 'кг'}`;
    case 'stock.set_min': return 'Неснижаемый запас сохранён';
    case 'stock.return': return 'Возврат остатка на склад записан';
    case 'payments.create': return 'Платёж записан';
    case 'payments.delete': return 'Платёж удалён';
    case 'attachments.confirm': return 'Файл загружен';
    case 'attachments.remove': return 'Файл убран';
    case 'attachments.mark_sent': return payload['sent'] === 'no' ? 'Отметка «отправлено» снята' : 'Отмечено: отправлено';
    case 'attachments.presign_upload': return null;
    case 'counterparties.create': return 'Контрагент создан';
    case 'counterparties.update': return 'Контрагент сохранён';
    case 'contacts.create': return 'Контакт добавлен';
    case 'product_models.create': return 'Модель создана';
    case 'product_models.update': return 'Модель сохранена';
    case 'model_materials.set': return 'Материалы и цены сохранены';
    case 'material_masters.create': return 'Материал создан';
    case 'colours.create': return 'Цвет создан';
    case 'decoration_types.create': return 'Вид нанесения создан';
    case 'decoration_types.update': return 'Ставки вида нанесения сохранены';
    case 'users.create': return 'Учётка создана';
    case 'users.set_password': return 'Пароль задан';
    case 'users.set_status': return 'Учётка обновлена';
    case 'product_categories.upsert': return 'Категория сохранена';
    case 'size_systems.upsert': return 'Шкала сохранена';
    default: return operation.endsWith('.update') || operation.endsWith('.upsert') || operation.endsWith('.set_status') ? 'Сохранено' : 'Готово';
  }
}

export function Toasts() {
  const [items, setItems] = useState<Toast[]>([]);
  useEffect(() => {
    const on: Listener = (t) => {
      setItems((xs) => [...xs, t]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== t.id)), t.tone === 'error' ? 8000 : 4500);
    };
    listeners.add(on);
    return () => { listeners.delete(on); };
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast toast--${t.tone}`} onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}>
          <span className="toast__icon">{t.tone === 'ok' ? '✓' : t.tone === 'warn' ? '⚠' : '✕'}</span>
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}
